import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useApi } from "src/api";
import { Page, PageStatus } from "src/components";
import { useLoaders } from "src/components/InlineLoader";
import { Button, Color, Flex, Pagination, SubgridTable, Text } from "src/elements";
import { usePaginationPage } from "src/elements/Pagination";
import { useAsyncState } from "src/hooks/useAsyncState";
import { EditableField } from "./components/EditableField";
import { ProductsSearcher } from "./components/ProductsSearcher";
import {
  ResponseGetInputsStoreProducts_storeId_,
  RouteGetInputsStoreProducts_storeId_,
} from "@CommonApi/inputs/store-products/_storeId_";
import { StoreProduct } from "@CommonApi/inputs/store-products";
import { Product } from "@CommonApi/inputs/products/all";

export const PageStoreProducts = () => {
  const { storeId: _storeId } = useParams<{
    storeId: string;
  }>();
  const storeId = Number(_storeId);
  const { getStoreProductsApi } = useApi();
  const { hasLoader } = useLoaders();

  const [response, setResponse] = useAsyncState<ResponseGetInputsStoreProducts_storeId_>();
  const [page, setPage] = usePaginationPage();
  const size = 10;
  const refSearch = useRef("");

  const isLoading = hasLoader(RouteGetInputsStoreProducts_storeId_);

  useEffect(() => {
    setResponse(getStoreProductsApi({ storeId, page, size, search: refSearch.current }));
  }, [page]);

  const updateProduct = (
    updatedProduct: StoreProduct & Pick<Product, "name" | "product_code" | "upc">,
  ) => {
    if (response) {
      setResponse({
        ...response,
        data: response.data.map((product) =>
          product.store_product_id === updatedProduct.store_product_id
            ? { ...product, ...updatedProduct }
            : product,
        ),
      });
    }
  };

  return (
    <Page>
      <Text variant="h3">Store {storeId} Products</Text>

      <Flex justify="between" align="center">
        <ProductsSearcher
          storeId={storeId}
          page={page}
          size={size}
          refSearch={refSearch}
          setResponse={setResponse}
        />

        <Button isDisabled iconRight={{ name: "plus" }}>
          Add Product
        </Button>
      </Flex>

      <Flex flexGrow>
        {isLoading && <PageStatus label="Loading store products..." icon="spinner" />}

        {response && !isLoading && (
          <SubgridTable
            headersNoWrap
            rowBackground={(row) => (row.is_new_item && Color.greenSmokeHover) || undefined}
            columns={[
              { header: "Store product id", id: "store_product_id" },
              { header: "Store id", id: "store_id" },
              { header: "Store code", id: "store_code" },
              { header: "Product id", id: "product_id" },
              { header: "Product code", id: "product_code" },
              { header: "Name", id: "name" },
              { header: "Upc", id: "upc" },
              {
                header: "Cdt0",
                id: "cdt0",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNullable
                  />
                ),
              },
              {
                header: "Cdt1",
                id: "cdt1",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNullable
                  />
                ),
              },
              {
                header: "Cdt2",
                id: "cdt2",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNullable
                  />
                ),
              },
              {
                header: "Cdt3",
                id: "cdt3",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNullable
                  />
                ),
              },
              {
                header: "Cdt1 priority",
                id: "cdt1_priority",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "Cdt2 priority",
                id: "cdt2_priority",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "Cdt3 priority",
                id: "cdt3_priority",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "Quantity",
                id: "quantity",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "Profit",
                id: "profit",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "Sales amount",
                id: "sales_amount",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "Dispersion",
                id: "dispersion",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "Min days of supply",
                id: "min_days_of_supply",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "Max days of supply",
                id: "max_days_of_supply",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "Min on shelf",
                id: "min_on_shelf",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "Min facings",
                id: "min_facings",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "Max facings",
                id: "max_facings",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "Adjustment x",
                id: "adjustment_x",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "Adjustment y",
                id: "adjustment_y",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "Adjustment z",
                id: "adjustment_z",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "Notes",
                id: "notes",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNullable
                  />
                ),
              },
              {
                header: "Special zone ban",
                id: "special_zone_ban",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNullable
                  />
                ),
              },
              {
                header: "Scenario1",
                id: "scenario1",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNullable
                  />
                ),
              },
              {
                header: "Scenario2",
                id: "scenario2",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNullable
                  />
                ),
              },
              {
                header: "Scenario3",
                id: "scenario3",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNullable
                  />
                ),
              },
              { header: "Created by", id: "created_by" },
              { header: "Created at", id: "created_at" },
              {
                header: "Price",
                id: "price",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "Order num",
                id: "order_num",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "Divider width",
                id: "divider_width",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "Divider height",
                id: "divider_height",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "Squeeze width pct",
                id: "squeeze_width_pct",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "Squeeze height pct",
                id: "squeeze_height_pct",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "Squeeze depth pct",
                id: "squeeze_depth_pct",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "Nesting height",
                id: "nesting_height",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "Nesting width",
                id: "nesting_width",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "Nesting depth",
                id: "nesting_depth",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "Peg code",
                id: "peg_code",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNullable
                  />
                ),
              },
              {
                header: "Item hang hole x",
                id: "item_hang_hole_x",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "Item hang hole y",
                id: "item_hang_hole_y",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "Gtin",
                id: "gtin",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNullable
                  />
                ),
              },
              {
                header: "Mpl",
                id: "mpl",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "Min stack",
                id: "min_stack",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "Max stack",
                id: "max_stack",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                  />
                ),
              },
              {
                header: "No of units in case",
                id: "no_of_units_in_case",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "No of units in tray",
                id: "no_of_units_in_tray",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNumber
                    isNullable
                  />
                ),
              },
              {
                header: "Merchandising style",
                id: "merchandising_style",
                renderer: (row) => (
                  <EditableField id={row.id} updateProduct={updateProduct} product={row.row} />
                ),
              },
              {
                header: "Flavour",
                id: "flavour",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isNullable
                  />
                ),
              },
              {
                header: "Shelf type",
                id: "shelf_type",
                renderer: (row) => (
                  <EditableField id={row.id} updateProduct={updateProduct} product={row.row} />
                ),
              },
              {
                header: "Core Range",
                id: "is_core_range",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isBoolean={true}
                  />
                ),
              },
              {
                header: "OOS",
                id: "is_oos",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isBoolean={true}
                  />
                ),
              },
              {
                header: "New Product",
                id: "is_new_item",
                renderer: (row) => (
                  <EditableField
                    id={row.id}
                    updateProduct={updateProduct}
                    product={row.row}
                    isBoolean={true}
                  />
                ),
              },
            ]}
            data={response.data}
          />
        )}
      </Flex>

      <Pagination
        page={page}
        setPage={setPage}
        totalPages={response?.totalPages}
        isLoading={isLoading}
      />
    </Page>
  );
};
